import { useState } from 'react';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  AdministrationLayout,
  Badge,
  Button,
  CardActions,
  ChoiceGroup,
  Cluster,
  DialogActions,
  EmptyState,
  Form,
  FormField,
  HeaderActions,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlanTeaser,
  RowActions,
  Select,
  Skeleton,
  Split,
  StatusChip,
  Surface,
  Table,
  Tabs,
  TeamTree,
  THead,
  TBody,
  TR,
  TH,
  TD,
  useZodForm,
} from '../components/ui';
import type { ActionDescriptor, TabItem, TeamTreeNode } from '../components/ui';
import { RoleSelect } from '../components/access/RoleSelect';
import { PermissionMatrix } from '../components/access/PermissionMatrix';
import { DeleteRoleDialog } from '../components/access/DeleteRoleDialog';
import { useAuth } from '../lib/auth';
import { useCapabilities } from '../lib/capabilities';
import { isQuotaError } from '../lib/quotaEvents';
import { errorMessage } from '../lib/errorMessage';
import { useCursorList } from '../lib/query/useCursorList';
import {
  listRoles,
  listMembers,
  listTeams,
  permissionCatalog,
  createRole,
  updateRole,
  updateMember,
  inviteMember,
  resendInvite,
  removeMember,
  createTeam,
  accessKeys,
  InviteMemberBodySchema,
  type Role,
  type Member,
  type Team,
  type UpdateRoleBody,
  type UpdateMemberBody,
} from '../lib/api/access.gen';
import { convertOrg, ConvertOrgBodySchema, type ConvertOrgBody } from '../lib/api/orgs.gen';

// G-26 (story 2-95b): composed over the generated contract schemas — never
// hand-declared. `roleIds` (the wire array) becomes a single-select
// `roleId` UI field (this dialog only ever assigns ONE role at invite time);
// mapped back to `roleIds: [roleId]` at submit. `kind` is always 'staff'
// here (the Clients tab doesn't exist yet per this file's own header
// comment), so it is omitted rather than exposed as a field with one value.
const inviteMemberFormSchema = InviteMemberBodySchema.omit({ kind: true, roleIds: true }).extend({
  roleId: z.string().optional(),
});
// `ConvertOrgBodySchema` is used as-is — every field on that dialog is
// exactly the wire body, no UI-local field to compose in. The Create-team
// form (below, in the Teams tab) stays a plain `<form onSubmit>` rather than
// a third `Form`/`useZodForm` composition: its `parentTeamId` field is
// driven by `TeamTree`'s own selection state, not a registered RHF control,
// so there is no real resolver-shaped value to wire up (see that form's own
// comment).

// Org Access console (P01 §4–5, story 1C-3). Members + Roles + Teams tabs.
// Mutations refetch the capability document immediately (the governance SSE also
// fires, so other open tabs refresh too). Teams (story 2-10) is the minimal
// TeamTree area: list in tree order + a create control with the parentTeamId
// nesting seam — richer team management is a later surface. Simplifications
// noted for later: no Clients tab (Wave 6 grants), role naming uses a prompt (a
// modal comes with the design pass).

export function OrgAccessPage(): JSX.Element {
  const { user } = useAuth();
  const { refetch: refetchCapabilities, featureState, doc, hasPermission } = useCapabilities();
  // A personal solo workspace hides team/invite affordances (story 2-13) and
  // offers "Convert to organization" instead (story 2-14, owner-only).
  const isPersonal = doc.orgKind === 'personal';
  const canConvert = isPersonal && hasPermission('org:billing:manage');
  // Custom roles is a plan feature (org.customRoles). When it isn't 'on' the
  // backend rejects role mutations (story 1G-4) — preempt with a teaser + disable
  // the create/duplicate affordances instead of letting them 403.
  const customRolesLocked = featureState('org.customRoles').state !== 'on';
  const queryClient = useQueryClient();
  const orgId = user?.organizationId ?? '';

  const [tab, setTab] = useState<'members' | 'roles' | 'teams'>('members');
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  // Role delete (story 2-84, P01 §228). Holds the target of a pending delete;
  // the dialog itself owns the mutation (DeleteRoleDialog.tsx) — see that
  // file for the two-step "attempt, then reassign on 409" flow.
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Invite-member modal (story 2-5, W2 step 3). G-26 (story 2-95b): a real
  // `Form`/`useZodForm` composition over `inviteMemberFormSchema`, not
  // `useState` fields.
  const [inviteOpen, setInviteOpen] = useState(false);
  const inviteForm = useZodForm(inviteMemberFormSchema, { defaultValues: { email: '', roleId: '' } });
  // Email-undelivered warning banner (story 2-25): an invite/resend can
  // succeed at the mutation while the notification email fails to send (e.g.
  // an email-provider outage) — this is NOT an error, so it renders as a
  // status banner (role="status") naming the affected member, never a
  // destructive-toned toast claiming the invite failed.
  const [inviteWarning, setInviteWarning] = useState<{ memberId: string; email: string; message: string } | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  // Team-create control (story 2-10): name + optional parent (nesting seam).
  const [teamName, setTeamName] = useState('');
  const [teamParentId, setTeamParentId] = useState('');
  // Convert Personal → Organization dialog (story 2-14): name + plan choice.
  // G-26 (story 2-95b): a real `Form`/`useZodForm` composition over
  // `ConvertOrgBodySchema` directly, not `useState` fields.
  const [convertOpen, setConvertOpen] = useState(false);
  const convertForm = useZodForm(ConvertOrgBodySchema, { defaultValues: { name: '', planSlug: 'free' } });

  const rolesQuery = useQuery({
    queryKey: accessKeys.list({ org: orgId, scope: 'roles' }),
    queryFn: () => listRoles(orgId).then((r) => r.data),
    enabled: Boolean(orgId),
  });
  // Story 2-72d: off the inline `['access','members',orgId]` key onto the
  // generated `accessKeys` factory (G-19) + `useCursorList` (G-18), now that
  // `GET /orgs/{orgId}/members` answers the shared cursor envelope instead of
  // an unbounded `{ data: [...] }`. No search box is wired up here — `q` is a
  // real server filter (see `contracts/access.js`), but this page has never
  // had a member-search control and the packet scopes this story to the
  // query-key/pagination migration, not a new UI feature (same restraint
  // `2-72b` used for `users.gen.ts`'s unconsumed `name`/`role` filters).
  const membersList = useCursorList<Member>({
    queryKey: accessKeys.list({ org: orgId, scope: 'members' }),
    fetchPage: (pageParams) => listMembers(orgId, pageParams),
    enabled: Boolean(orgId),
  });
  const teamsQuery = useQuery({
    queryKey: accessKeys.list({ org: orgId, scope: 'teams' }),
    queryFn: () => listTeams(orgId).then((r) => r.data),
    enabled: Boolean(orgId) && tab === 'teams',
  });
  const catalogQuery = useQuery({
    queryKey: accessKeys.list({ org: orgId, scope: 'catalog' }),
    queryFn: () => permissionCatalog(orgId).then((r) => r.data),
    enabled: Boolean(orgId) && tab === 'roles',
  });

  const afterMutation = async () => {
    setError(null);
    await Promise.all([queryClient.invalidateQueries({ queryKey: accessKeys.all }), refetchCapabilities()]);
  };

  const roleUpdate = useMutation({
    mutationFn: (vars: { roleId: string; body: UpdateRoleBody }) => updateRole(orgId, vars.roleId, vars.body),
    onSuccess: afterMutation,
    onError: (e) => setError(errorMessage(e)),
  });
  const roleCreate = useMutation({
    mutationFn: (body: { cloneFrom: string; name: string }) => createRole(orgId, body),
    onSuccess: async (res) => {
      await afterMutation();
      setSelectedRoleId(res.data.id);
      setTab('roles');
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const memberUpdate = useMutation({
    mutationFn: (vars: { membershipId: string; body: UpdateMemberBody }) => updateMember(orgId, vars.membershipId, vars.body),
    onSuccess: afterMutation,
    onError: (e) => setError(errorMessage(e)),
  });
  const memberInvite = useMutation({
    mutationFn: (body: { email: string; kind: 'staff'; roleIds?: string[] }) => inviteMember(orgId, body),
    onSuccess: async (res) => {
      await afterMutation();
      setInviteOpen(false);
      inviteForm.reset({ email: '', roleId: '' });
      // The membership always lands; a delivery failure surfaces as a
      // dismissable status banner with a resend affordance, not an error.
      setInviteWarning(res.warning ? { memberId: res.data.id, email: res.data.email, message: res.warning.message } : null);
    },
    // Seat cap (story 2-38): a quota-shaped 402 is surfaced by the shared 2-16
    // QuotaTeaser (the apiClient interceptor) — skip the inline error bar so the
    // teaser is the single surface, no double render (same pattern as the 2-18
    // template editor).
    onError: (e) => {
      if (isQuotaError(e)) return;
      setError(errorMessage(e));
    },
  });
  const memberResend = useMutation({
    mutationFn: (membershipId: string) => resendInvite(orgId, membershipId),
    onMutate: (membershipId) => setResendingId(membershipId),
    onSettled: () => setResendingId(null),
    onSuccess: (res) => {
      setInviteWarning(res.warning ? { memberId: res.data.id, email: res.data.email, message: res.warning.message } : null);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  // Story 2-78: remove a member / revoke a pending invite — the seat-freeing
  // path. The backend is status-aware (invited → revoked, active/suspended →
  // removed) and rejects removing the last active owner (422 → inline error).
  const memberRemove = useMutation({
    mutationFn: (membershipId: string) => removeMember(orgId, membershipId),
    onMutate: (membershipId) => setRemovingId(membershipId),
    onSettled: () => setRemovingId(null),
    onSuccess: afterMutation,
    onError: (e) => setError(errorMessage(e)),
  });
  const teamCreate = useMutation({
    mutationFn: (body: { name: string; parentTeamId?: string }) => createTeam(orgId, body),
    onSuccess: async () => {
      await afterMutation();
      setTeamName('');
      setTeamParentId('');
    },
    onError: (e) => setError(errorMessage(e)),
  });
  // Convert flips orgKind on the capability doc (SSE also fires), so the Invite
  // affordance + Teams tab appear on refetch. A pro choice returns a Stripe
  // checkout URL — follow it (the org stays on its current plan until payment
  // confirms; checkout-return caveat is story 2-21).
  const orgConvert = useMutation({
    mutationFn: (body: ConvertOrgBody) => convertOrg(orgId, body),
    onSuccess: async (res) => {
      if (res.checkoutUrl) {
        window.location.assign(res.checkoutUrl);
        return;
      }
      await afterMutation();
      setConvertOpen(false);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const roles: Role[] = rolesQuery.data ?? [];
  const members: Member[] = membersList.items;
  const teams: Team[] = teamsQuery.data ?? [];
  // TeamTree consumes its own node shape ({id, name, kind, parentTeamId}) —
  // this is a straight field-subset map, not a transform of what `teams`
  // means; `depth` drops out because TeamTree derives depth itself from
  // `parentTeamId` rather than trusting a value the API also happens to send.
  const teamNodes: TeamTreeNode[] = teams.map((t) => ({ id: t.id, name: t.name, kind: t.kind, parentTeamId: t.parentTeamId }));
  const selectedRole = roles.find((r) => r.id === selectedRoleId) ?? null;

  const duplicateRole = (role: Role) => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('Name for the new role', `${role.name} copy`);
    if (name && name.trim()) roleCreate.mutate({ cloneFrom: role.key, name: name.trim() });
  };

  // 4-96 resolution: the Members/Roles/Teams switcher now composes the real
  // `Tabs` primitive (role="tablist"/roving-tabindex/arrow-key contract)
  // instead of a hand-rolled 3-button toggle with its own bg-primary/hover
  // classes. Same URL-free controlled state (`tab`/`setTab`) the toggle
  // already used — Tabs is controlled-only, so this is a drop-in swap, not a
  // state-model change. A personal solo workspace still hides Teams (2-13).
  const tabItems: TabItem[] = [
    { id: 'members', label: 'Members' },
    { id: 'roles', label: 'Roles' },
    ...(!isPersonal ? [{ id: 'teams', label: 'Teams' }] : []),
  ];

  return (
    <AdministrationLayout
      header={{
        title: 'Access & roles',
        description: 'Manage who is in your organization and what they can do.',
        // Story 2-147r proof-of-concept: HeaderActions replaces the
        // hand-rolled Button pair in PageHeader's own `actions` slot — same
        // two conditionally-rendered Buttons/labels/handlers, now
        // descriptor-driven (an empty array renders nothing, matching a
        // personal workspace with neither action available).
        actions: (
          <HeaderActions
            actions={
              [
                ...(!isPersonal
                  ? [
                      {
                        id: 'invite-member',
                        label: 'Invite member',
                        variant: 'primary',
                        onClick: () => {
                          setError(null);
                          setInviteOpen(true);
                        },
                      },
                    ]
                  : []),
                ...(canConvert
                  ? [
                      {
                        id: 'convert-to-org',
                        label: 'Convert to organization',
                        variant: 'primary',
                        onClick: () => {
                          setError(null);
                          setConvertOpen(true);
                        },
                      },
                    ]
                  : []),
              ] as ActionDescriptor[]
            }
          />
        ),
      }}
    >

      {inviteOpen && (
        <Modal label="Invite a member" onClose={() => setInviteOpen(false)}>
          <ModalHeader title="Invite a member" onClose={() => setInviteOpen(false)}>
            <p className="text-sm text-muted-foreground">
              They’ll get an email to set a password and join this organization.
            </p>
          </ModalHeader>
          <ModalBody>
          <Form
            form={inviteForm}
            id="invite-member-form"
            className="space-y-4"
            onSubmit={(values) => {
              const email = values.email.trim();
              memberInvite.mutate({ email, kind: 'staff', ...(values.roleId ? { roleIds: [values.roleId] } : {}) });
            }}
          >
            <FormField name="email" label="Email" required>
              {(field) => (
                <Input
                  type="email"
                  autoComplete="off"
                  className="w-full text-sm"
                  placeholder="person@company.com"
                  {...field}
                />
              )}
            </FormField>
            <FormField name="roleId" label="Role (optional)">
              {(field) => (
                <Select className="w-full text-sm" {...field}>
                  <option value="">No role selected</option>
                  {roles.filter((r) => r.kind === 'staff').map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Select>
              )}
            </FormField>
            {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          </Form>
          </ModalBody>
          <ModalFooter>
            <DialogActions
              size="sm"
              actions={[
                { id: 'cancel', label: 'Cancel', variant: 'ghost', onClick: () => setInviteOpen(false) },
                {
                  id: 'send',
                  label: memberInvite.isPending ? 'Sending…' : 'Send invite',
                  variant: 'primary',
                  htmlType: 'submit',
                  form: 'invite-member-form',
                  disabled: memberInvite.isPending || !inviteForm.watch('email')?.trim(),
                },
              ]}
            />
          </ModalFooter>
        </Modal>
      )}

      {convertOpen && (
        <Modal label="Convert to organization" onClose={() => setConvertOpen(false)}>
          <ModalHeader title="Convert to organization" onClose={() => setConvertOpen(false)}>
            <p className="text-sm text-muted-foreground">
              Your workspace becomes a named organization — same data, and you can invite your team.
            </p>
          </ModalHeader>
          <ModalBody>
          <Form
            form={convertForm}
            id="convert-org-form"
            className="space-y-4"
            onSubmit={(values) => {
              const name = values.name.trim();
              orgConvert.mutate({ name, ...(values.planSlug === 'pro' ? { planSlug: 'pro' as const } : {}) });
            }}
          >
            <FormField name="name" label="Organization name" required>
              {(field) => (
                <Input autoComplete="off" className="w-full text-sm" placeholder="Acme Inspections" {...field} />
              )}
            </FormField>
            <FormField name="planSlug" label="Plan" control="group">
              {() => {
                const planSlug = convertForm.watch('planSlug');
                return (
                  <>
                    {/* Story 2-147xd: `ChoiceGroup` (the 7th action host,
                        `ActionHosts.tsx`) replaces this hand-rolled
                        `Grid` + raw `Button` pair — same equal-width grid,
                        `aria-pressed`, `type="button"`, now descriptor-driven. */}
                    <ChoiceGroup
                      aria-label="Plan"
                      choices={[
                        { id: 'free', label: 'Free', variant: planSlug === 'pro' ? 'secondary' : 'primary', pressed: planSlug !== 'pro', onClick: () => convertForm.setValue('planSlug', 'free') },
                        { id: 'pro', label: 'Pro', variant: planSlug === 'pro' ? 'primary' : 'secondary', pressed: planSlug === 'pro', onClick: () => convertForm.setValue('planSlug', 'pro') },
                      ]}
                    />
                    <p className="text-xs text-muted-foreground">
                      {planSlug === 'pro'
                        ? 'You’ll finish payment at checkout — your organization stays on its current plan until then.'
                        : 'Stay on Free — upgrade to Pro anytime from Billing.'}
                    </p>
                  </>
                );
              }}
            </FormField>
            {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          </Form>
          </ModalBody>
          <ModalFooter>
            <DialogActions
              size="sm"
              actions={[
                { id: 'cancel', label: 'Cancel', variant: 'ghost', onClick: () => setConvertOpen(false) },
                {
                  id: 'convert',
                  label: orgConvert.isPending
                    ? 'Converting…'
                    : convertForm.watch('planSlug') === 'pro'
                      ? 'Convert & continue to payment'
                      : 'Convert',
                  variant: 'primary',
                  htmlType: 'submit',
                  form: 'convert-org-form',
                  disabled: orgConvert.isPending || !convertForm.watch('name')?.trim(),
                },
              ]}
            />
          </ModalFooter>
        </Modal>
      )}

      {deleteRoleTarget && (
        <DeleteRoleDialog
          orgId={orgId}
          role={deleteRoleTarget}
          roles={roles}
          onClose={() => setDeleteRoleTarget(null)}
        />
      )}

      {error && <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}

      {inviteWarning && (
        <Cluster
          justify="between"
          gap={3}
          className="rounded-xl bg-warning/[0.12] px-4 py-3 text-sm text-foreground"
          role="status"
        >
          <span>
            <span className="font-medium">{inviteWarning.email}:</span> {inviteWarning.message}
          </span>
          <CardActions
            actions={[
              {
                id: 'resend',
                label: memberResend.isPending ? 'Resending…' : 'Resend invite',
                variant: 'secondary',
                disabled: memberResend.isPending,
                onClick: () => memberResend.mutate(inviteWarning.memberId),
              },
              {
                id: 'dismiss',
                label: 'Dismiss',
                variant: 'ghost',
                onClick: () => setInviteWarning(null),
              },
            ]}
          />
        </Cluster>
      )}

      <Tabs items={tabItems} value={tab} onChange={(next) => setTab(next as typeof tab)} aria-label="Access sections">
        {tab === 'members' &&
          (membersList.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : membersList.isError ? (
            // Bug fix (live user report): this branch didn't exist — a real
            // fetch failure (a permission/plan gate, a network error) fell
            // through to `members.length === 0` and rendered the exact same
            // "No members yet" copy as an org that genuinely has none, which
            // is a false empty state hiding a real error (G-27).
            <EmptyState
              title="Members could not be loaded"
              description={errorMessage(membersList.error)}
              actionLabel="Retry"
              onAction={() => membersList.refetch()}
            />
          ) : members.length === 0 ? (
            <EmptyState title="No members yet" description="Invited and active members will appear here." />
          ) : (
            <>
            <Table aria-label="Members">
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Email</TH>
                  <TH>Kind</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH> </TH>
                </TR>
              </THead>
              <TBody>
                {members.map((member) => {
                  // 2-147we: per-row Resend/Suspend/Revoke/Remove converge
                  // onto `RowActions` (2-147r's governed table/list-row
                  // host) instead of a hand-rolled `flex justify-end gap-2`
                  // cluster — at most 2 actions ever render simultaneously
                  // (one of Resend/Suspend-Reactivate, plus one of
                  // Revoke/Remove), exactly RowActions' own `maxVisible={2}`
                  // default, so nothing collapses into its overflow menu.
                  const rowActions: ActionDescriptor[] =
                    member.status === 'invited'
                      ? [
                          {
                            id: 'resend',
                            label: resendingId === member.id ? 'Resending…' : 'Resend invite',
                            variant: 'ghost',
                            disabled: resendingId === member.id,
                            onClick: () => memberResend.mutate(member.id),
                          },
                          {
                            id: 'revoke',
                            label: removingId === member.id ? 'Revoking…' : 'Revoke',
                            variant: 'destructive',
                            disabled: removingId === member.id,
                            onClick: () => {
                              // eslint-disable-next-line no-alert
                              if (window.confirm(`Cancel the pending invite for ${member.email}? This frees a seat.`)) {
                                memberRemove.mutate(member.id);
                              }
                            },
                          },
                        ]
                      : [
                          {
                            id: 'suspend-reactivate',
                            label: member.status === 'suspended' ? 'Reactivate' : 'Suspend',
                            variant: 'ghost',
                            disabled: memberUpdate.isPending,
                            onClick: () =>
                              memberUpdate.mutate({
                                membershipId: member.id,
                                body: { status: member.status === 'suspended' ? 'active' : 'suspended' },
                              }),
                          },
                          {
                            id: 'remove',
                            label: removingId === member.id ? 'Removing…' : 'Remove',
                            variant: 'destructive',
                            disabled: removingId === member.id,
                            onClick: () => {
                              // eslint-disable-next-line no-alert
                              if (
                                window.confirm(
                                  `Remove ${member.name || member.email} from this organization? They lose access immediately and their seat is freed.`
                                )
                              ) {
                                memberRemove.mutate(member.id);
                              }
                            },
                          },
                        ];
                  return (
                    <TR key={member.id}>
                      {/* 4-100: Name/Email are the identity columns a long real value
                          (a person's full name, a long email) is genuinely likely to
                          overflow — `truncate` caps the column instead of forcing the
                          whole table into horizontal scroll; `title` restores the full
                          value on hover/focus. */}
                      <TD truncate title={member.name || '—'}>{member.name || '—'}</TD>
                      <TD truncate title={member.email} className="text-muted-foreground">{member.email}</TD>
                      <TD>
                        <Badge tone="neutral">{member.kind}</Badge>
                      </TD>
                      <TD>
                        <RoleSelect
                          roles={roles}
                          kind={member.kind}
                          value={member.roleIds[0]}
                          disabled={memberUpdate.isPending}
                          onChange={(roleId) => memberUpdate.mutate({ membershipId: member.id, body: { roleIds: [roleId] } })}
                        />
                      </TD>
                      <TD>
                        {/* 2-147: lifecycle-state rendering (active/suspended/
                            invited) — same duplication class 2-141 converged
                            onto StatusChip; this page's own tone-ternary was
                            the one the 2-141/2-146d sweep missed. */}
                        <StatusChip
                          tone={member.status === 'active' ? 'success' : member.status === 'suspended' ? 'destructive' : 'warning'}
                          label={member.status}
                          value={member.status}
                        />
                      </TD>
                      <TD>
                        <RowActions actions={rowActions} className="justify-end" />
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
            {/* Story 2-72d: the roster is now a cursor page (default 50), not
                an unbounded `{ data: [...] }` read — an org past the default
                page size (rare; memberships are seat-capped) needs an
                affordance to reach the rest, or the migration would silently
                truncate a roster the pre-migration page always showed whole. */}
            {membersList.hasMore && (
              <Cluster justify="center" className="pt-3">
                <CardActions
                  size="sm"
                  actions={[
                    {
                      id: 'load-more-members',
                      label: membersList.isLoadingMore ? 'Loading…' : 'Load more',
                      variant: 'secondary',
                      disabled: membersList.isLoadingMore,
                      onClick: () => membersList.fetchNextPage(),
                    },
                  ]}
                />
              </Cluster>
            )}
            </>
          ))}

        {tab === 'teams' && !isPersonal && (
          // Split (2-147sb) — this exact `[1fr_20rem]` shape is the ratio's
          // own named precedent (`Layout.tsx`'s `Split` header comment);
          // `'3:1'` is the nearest named proportional ratio to the fixed
          // content:panel split this page used to hand-roll.
          <Split
            gap={6}
            ratio="3:1"
            primary={
              <Surface as="div" variant="flat" padding="lg">
                {teamsQuery.isLoading ? (
                  <Skeleton className="h-40 w-full" />
                ) : teamsQuery.isError ? (
                  <EmptyState
                    title="Teams could not be loaded"
                    description={errorMessage(teamsQuery.error)}
                    actionLabel="Retry"
                    onAction={() => void teamsQuery.refetch()}
                  />
                ) : teams.length === 0 ? (
                  <EmptyState title="No teams yet" description="Create a team to group members by region or branch." />
                ) : (
                  // TeamTree (story 2-146d, P10 §2.2) replaces the flat
                  // depth-indented `<ul>` this page used to hand-roll: the real
                  // nested-checkbox-tree contract, not a stand-in for it. This
                  // tree also IS the create form's parent picker
                  // (`selectionMode="single"`) — checking a team here sets
                  // `teamParentId` for the form on the right, replacing the
                  // `<Select>` dropdown that used to sit there; the mutation
                  // itself (`teamCreate`, `parentTeamId`) is unchanged.
                  <TeamTree
                    aria-label="Teams"
                    nodes={teamNodes}
                    value={teamParentId ? new Set([teamParentId]) : new Set<string>()}
                    onChange={(next) => setTeamParentId([...next][0] ?? '')}
                    selectionMode="single"
                  />
                )}
              </Surface>
            }
            secondary={
              // eslint-disable-next-line design-language/no-raw-control -- documented exception (see this file's header comment above `inviteMemberFormSchema`): parentTeamId is driven by TeamTree's own selection state, not a registered RHF control, so there is no Form/useZodForm composition to wire up here (story 2-147xe)
              <form
                className="rounded-hairline border border-border bg-card h-fit space-y-4 p-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = teamName.trim();
                  if (!name) return;
                  teamCreate.mutate({ name, ...(teamParentId ? { parentTeamId: teamParentId } : {}) });
                }}
              >
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Create a team</h3>
                  <p className="text-xs text-muted-foreground">
                    Teams nest — pick a parent to build region › branch › team structures.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="team-name">Name</label>
                  <Input
                    id="team-name"
                    required
                    autoComplete="off"
                    className="w-full text-sm"
                    placeholder="Brisbane North"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                  />
                </div>
                <Cluster gap={2} className="text-sm">
                  <span className="font-medium text-foreground">Parent:</span>
                  <Badge tone="neutral">
                    {teamParentId ? teams.find((t) => t.id === teamParentId)?.name ?? teamParentId : 'Organization root'}
                  </Badge>
                  <RowActions
                    actions={teamParentId ? [{ id: 'clear', label: 'Clear', variant: 'ghost', onClick: () => setTeamParentId('') }] : []}
                  />
                </Cluster>
                {/* Story 2-147xd: `CardActions` + `ActionDescriptor.htmlType`
                    (grown for exactly this shape — see ActionHosts.tsx's own
                    doc comment). This is a plain-state `<form onSubmit>` (not
                    react-hook-form, so `FormActions`'s RHF-aware `FormSubmit`
                    doesn't apply) — `htmlType: 'submit'` with no `onClick`
                    renders a real native submit control that fires the
                    surrounding form's own `onSubmit`, no double-fire risk. */}
                <CardActions
                  size="sm"
                  actions={[
                    {
                      id: 'create-team',
                      label: teamCreate.isPending ? 'Creating…' : 'Create team',
                      variant: 'primary',
                      htmlType: 'submit',
                      disabled: teamCreate.isPending || !teamName.trim(),
                    },
                  ]}
                />
              </form>
            }
          />
        )}

        {tab === 'roles' && customRolesLocked && (
          <PlanTeaser feature="org.customRoles" name="Custom Roles" className="mb-4" />
        )}

        {tab === 'roles' && (
          // Ratio `'1:3'` is the nearest named proportional ratio to this
          // page's own hand-rolled `[18rem_1fr]` fixed-width role list.
          <Split
            gap={6}
            ratio="1:3"
            primary={
              <div className="space-y-2">
                {rolesQuery.isLoading ? (
                  <Skeleton className="h-64 w-full" />
                ) : rolesQuery.isError ? (
                  <EmptyState
                    title="Roles could not be loaded"
                    description={errorMessage(rolesQuery.error)}
                    actionLabel="Retry"
                    onAction={() => void rolesQuery.refetch()}
                  />
                ) : (
                  roles.map((role) => (
                    <Surface
                      key={role.id}
                      as="div"
                      variant="flat"
                      padding="sm"
                      className={clsx(role.id === selectedRoleId && 'ring-1 ring-primary')}
                    >
                      <Cluster gap={2} justify="between">
                        {/* Story 2-147xd: left as a direct `Button` — this is a
                            SELECTION control (choosing which role's permission
                            matrix shows on the right), not a command, and its
                            visible content is two stacked lines (name + system/
                            custom · kind), not a plain string `label`.
                            `ActionDescriptor.label` is `string`-typed on
                            purpose (this file's own "NO REACTNODE ESCAPE
                            HATCH" section, ActionHosts.tsx) — no governed host
                            can render this without either truncating the
                            second line or reopening a ReactNode escape, the
                            same class of gap `CreateTemplateDialog.tsx`'s own
                            starter-picker Button already documents. */}
                        <Button
                          variant="ghost"
                          size="fit"
                          className="block min-w-0 flex-1 justify-start text-left"
                          onClick={() => setSelectedRoleId(role.id)}
                        >
                          <span className="block truncate text-sm font-semibold text-foreground">{role.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {role.isSystem ? 'System' : 'Custom'} · {role.kind}
                          </span>
                        </Button>
                        <CardActions
                          actions={[
                            {
                              id: 'duplicate',
                              label: 'Duplicate',
                              variant: 'ghost',
                              disabled: roleCreate.isPending || customRolesLocked,
                              onClick: () => duplicateRole(role),
                            },
                            // Story 2-84: system roles are never deletable
                            // (backend 400s them anyway; hidden here rather
                            // than offered-then-refused). Same Pro-gate as
                            // Duplicate/edit — org.customRoles locked means
                            // the mutating role endpoints 403 on a flag, so
                            // Delete stays disabled alongside it.
                            ...(!role.isSystem
                              ? [
                                  {
                                    id: 'delete',
                                    label: 'Delete',
                                    variant: 'destructive' as const,
                                    disabled: customRolesLocked,
                                    onClick: () => setDeleteRoleTarget(role),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </Cluster>
                    </Surface>
                  ))
                )}
              </div>
            }
            secondary={
              <Surface as="div" variant="flat" padding="lg">
                {!selectedRole ? (
                  <p className="text-sm text-muted-foreground">Select a role to view or edit its permissions.</p>
                ) : catalogQuery.isError ? (
                  <p className="text-sm text-destructive">Couldn’t load the permission catalog. Please try again.</p>
                ) : !catalogQuery.data ? (
                  <Skeleton className="h-64 w-full" />
                ) : (
                  <PermissionMatrix
                    key={selectedRole.id}
                    role={selectedRole}
                    catalog={catalogQuery.data}
                    // Editing is gated on the Pro custom-roles feature; when it's locked the
                    // matrix is view-only (like system roles), so viewing a role never offers a
                    // Save that the masked update endpoint would reject (story 1G-8).
                    canEdit={!selectedRole.isSystem && !customRolesLocked}
                    saving={roleUpdate.isPending}
                    error={error}
                    onSave={(permissions) => roleUpdate.mutate({ roleId: selectedRole.id, body: { permissions } })}
                  />
                )}
              </Surface>
            }
          />
        )}
      </Tabs>
    </AdministrationLayout>
  );
}
